import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, ArrowRight, Info, AlertTriangle, Check,
  Search, Stethoscope, Video, ExternalLink, Box, Phone, MapPin, Clock,
  CalendarPlus, Copy, Loader2, Cross,
  Heart, Eye, Wind, Baby, Brain, Activity, Flower2, Droplet, MoreHorizontal,
  ShieldAlert, UserRound, HelpCircle, CalendarClock, EyeOff, Smartphone, X,
} from "lucide-react";
import { toast } from "sonner";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Une erreur est survenue.");
const cleanPhone = (v) => (v || "").replace(/[\s.\-]/g, "");
const isPhoneValid = (v) => { const p = cleanPhone(v); return /^0[67]\d{8}$/.test(p) || /^\+33[67]\d{8}$/.test(p); };
const RDV_CONSENT = "J'accepte que mes données personnelles (nom, prénom) soient traitées par la pharmacie uniquement pour gérer et confirmer mon rendez-vous. Conservation limitée, aucun partage à des fins commerciales. Je peux exercer mes droits (accès, rectification, effacement) auprès de la pharmacie.";
const hmFr = (iso) => iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }).replace(":", "h") : "—";
const dayLabel = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Paris" });
const DOCTOLIB_LOCATION_SLUG = "la-londe-les-maures";

const PARTNER_DOMAINS = ["doctolib.fr", "qare.fr", "livi.fr", "maiia.com", "hellocare.com"];
function openExternal(url) {
  try {
    const u = new URL(url);
    if (u.protocol === "https:" && PARTNER_DOMAINS.some((d) => u.hostname.endsWith(d))) {
      window.open(url, "_blank", "noopener,noreferrer"); return true;
    }
  } catch (e) {}
  return false;
}

// Orientation : ces éléments servent UNIQUEMENT à la navigation et aux messages de sécurité.
// Aucun stockage, aucun score, aucun diagnostic.
const GENERALIST_SLUG = "medecin-generaliste";
const DIRECT_ACCESS = new Set(["gynecologie", "ophtalmologie", "psychiatrie", "pediatrie", "stomatologie", "chirurgie-orale"]);
const SPEC_NEEDS = [
  { slug: "cardiologie", label: "Cœur / cardiologie", icon: Heart },
  { slug: "dermatologie", label: "Peau / dermatologie", icon: Droplet },
  { slug: "ophtalmologie", label: "Vue / ophtalmologie", icon: Eye },
  { slug: "pneumologie", label: "Respiration / pneumologie", icon: Wind },
  { slug: "gynecologie", label: "Gynécologie", icon: Flower2 },
  { slug: "endocrinologie", label: "Hormones / diabète", icon: Activity },
  { slug: "psychiatrie", label: "Psychiatrie", icon: Brain },
  { slug: "pediatrie", label: "Pédiatrie", icon: Baby },
  { slug: "__autre", label: "Autre", icon: MoreHorizontal },
];
const MOTIFS = ["Avis médical", "Fièvre", "Douleur", "Renouvellement / ordonnance", "Autre"];
const NON_DIAGNOSTIC_NOTE = "Vos réponses permettent uniquement de vous proposer des possibilités de prise en charge. Elles ne constituent pas un diagnostic médical.";

const BigCard = ({ icon: Icon, title, subtitle, onClick, testid, disabled }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className="w-full text-left rounded-2xl border-2 p-5 hover:border-primary hover:bg-primary/5 transition-colors flex items-center gap-4 disabled:opacity-50">
    {Icon && <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center text-primary shrink-0"><Icon className="h-6 w-6" /></div>}
    <div className="min-w-0 flex-1"><div className="font-heading font-bold text-base leading-tight">{title}</div>{subtitle && <div className="text-sm text-muted-foreground mt-1">{subtitle}</div>}</div>
    <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
  </button>
);
const InfoBox = ({ children }) => (
  <div className="rounded-xl bg-secondary/5 border border-secondary/20 p-3 mt-6 flex gap-2">
    <Info className="h-4 w-4 text-secondary shrink-0 mt-0.5" /><p className="text-xs text-muted-foreground leading-relaxed">{children}</p>
  </div>
);
const HelpNote = () => (
  <div className="rounded-xl bg-muted/40 p-3 mt-6 text-xs text-muted-foreground leading-relaxed">L'équipe de la pharmacie est à vos côtés pour vous aider à utiliser ce service et à organiser votre téléconsultation.</div>
);
const ReimbursementNote = () => (
  <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">La prise en charge par l'Assurance Maladie et votre complémentaire dépend notamment de votre situation, de la spécialité consultée et du respect du parcours de soins.</p>
);
const UrgenceLink = ({ onClick }) => (
  <button data-testid="tc-urgence-link" onClick={onClick} className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:underline"><ShieldAlert className="h-4 w-4" /> Signes d'alerte / urgence</button>
);
const LegalLine = ({ children }) => (<p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">{children}</p>);

// Reprise sur téléphone personnel : le QR encode uniquement l'URL du parcours EYMI,
// sans aucune donnée personnelle ni médicale.
const PhoneContinue = ({ url, testid }) => (
  <Card className="rounded-2xl p-4 flex items-center gap-4" data-testid={testid}>
    <div className="bg-white p-1.5 rounded-lg shrink-0 border"><QRCodeSVG value={url} size={92} level="M" bgColor="#ffffff" fgColor="#0f172a" /></div>
    <div className="min-w-0">
      <p className="font-heading font-bold text-sm flex items-center gap-1.5"><Smartphone className="h-4 w-4 text-primary" /> Continuer sur mon téléphone</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">Scannez pour reprendre en privé sur votre téléphone. Aucune donnée personnelle ni médicale n'est contenue dans ce code.</p>
    </div>
  </Card>
);

const NOTE_ORIENTATION = "Eymi vous oriente vers une plateforme de réservation indépendante. La prise de rendez-vous et la collecte éventuelle de vos coordonnées sont effectuées directement sur cette plateforme.";
const NOTE_CABIN = "Vos nom et prénom sont utilisés uniquement par la pharmacie pour réserver votre cabine et vous confirmer le créneau (conservation limitée). Aucune donnée médicale n'est transmise aux plateformes de rendez-vous.";

// Comptage anonyme : simple incrément d'un compteur agrégé, sans identifiant ni historique.
const countSpecialty = (slug, pharmacyId) => {
  const s = slug === "medecin-generaliste" ? "medecine-generale" : slug;
  if (s) api.post("/public/stats/specialty", { specialtySlug: s, pharmacyId: pharmacyId || null }).catch(() => {});
};

function Shell({ children, progress, pharmacyName, session, cabin }) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {session?.sensitive && (
        <div className="fixed top-3 right-3 z-40">
          <Button data-testid="tc-end-session" onClick={session.endSession} variant="outline" size="sm" className="rounded-full gap-1.5 bg-background/90 backdrop-blur shadow-md border-2">
            <X className="h-4 w-4" /> Terminer ma session
          </Button>
        </div>
      )}
      <div className="mx-auto max-w-md px-5 py-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground"><Cross className="h-5 w-5" /></div>
          <div className="font-heading font-bold leading-tight">Téléconsultation spécialiste<div className="text-[11px] font-normal text-muted-foreground">{pharmacyName || "Pharmacie"}</div></div>
        </div>
        {progress != null && <Progress value={progress} className="h-1.5 mb-6" />}
        {children}
      </div>
      <footer className="border-t bg-muted/30 text-[11px] text-muted-foreground px-4 py-4 text-center space-y-1">
        <p className="font-semibold text-destructive">En cas d'urgence, contactez le 15 ou le 112.</p>
        <p data-testid="tc-privacy-note">{cabin ? NOTE_CABIN : NOTE_ORIENTATION}</p>
      </footer>
      {session?.idleWarn && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-6" data-testid="tc-idle-warning" role="alertdialog" aria-modal="true">
          <div className="bg-card text-card-foreground rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl border">
            <div className="h-14 w-14 rounded-full bg-amber-500/15 grid place-items-center mx-auto mb-3"><Clock className="h-7 w-7 text-amber-600" /></div>
            <h2 className="font-heading text-xl font-bold mb-1.5">Toujours là ?</h2>
            <p className="text-sm text-muted-foreground mb-5">Sans activité de votre part, cette session va se fermer et vos informations seront effacées dans <b className="text-foreground" data-testid="tc-idle-countdown">{session.idleLeft}</b> s.</p>
            <Button data-testid="tc-idle-stay" onClick={session.stay} className="w-full h-12 rounded-2xl mb-2">Continuer ma session</Button>
            <Button data-testid="tc-idle-end" variant="ghost" onClick={session.endSession} className="w-full h-11 rounded-2xl text-muted-foreground">Terminer maintenant</Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Back = ({ onClick }) => (
  <button onClick={onClick} data-testid="tc-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Retour</button>
);

export default function TeleconsultationSpecialiste() {
  const navigate = useNavigate();
  const { pharmacySlug } = useParams();
  const [searchParams] = useSearchParams();
  // Ouvert via le QR code de la borne : le patient poursuit seul sur son téléphone
  const fromPhone = searchParams.get("etape") === "specialites";
  const [config, setConfig] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(fromPhone ? "specialite" : "mt_indispo");
  const [loading, setLoading] = useState(false);

  const [req, setReq] = useState(null);
  const [specSlug, setSpecSlug] = useState(null);
  const [specQuery, setSpecQuery] = useState("");

  const [slots, setSlots] = useState([]);
  const [coord, setCoord] = useState({ firstName: "", lastName: "" });
  const [consentRdv, setConsentRdv] = useState(false);
  const [appt, setAppt] = useState({ date: "", time: "" });
  const [appointmentStart, setAppointmentStart] = useState(null);
  const [chosenPartner, setChosenPartner] = useState(null);
  const [held, setHeld] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [booking, setBooking] = useState(null);
  const [showNameForm, setShowNameForm] = useState(fromPhone);
  // Orientation (UI seulement, non stocké)
  const [needSlug, setNeedSlug] = useState(null);
  const [medOption, setMedOption] = useState(null);
  const [motif, setMotif] = useState(null);
  const [answers, setAnswers] = useState({});
  const [alertFlag, setAlertFlag] = useState(false);

  // --- Confidentialité borne : fermeture automatique de session ---
  const SENSITIVE_STEPS = useMemo(() => new Set([
    "qr_telephone", "specialite", "consulter_medecin", "besoin_specialiste",
    "questions", "solutions", "recherche", "avant_redirection", "horaire",
    "cabine", "creneau_confirme", "cabine_expiree", "confirmation",
  ]), []);
  const isSensitive = !fromPhone && SENSITIVE_STEPS.has(step);
  const [idleWarn, setIdleWarn] = useState(false);
  const [idleLeft, setIdleLeft] = useState(10);
  const lastActivity = useRef(Date.now());

  const resetSession = useCallback(() => {
    // Efface toutes les informations temporaires du patient puis revient à l'accueil.
    setReq(null); setSpecSlug(null); setSpecQuery("");
    setSlots([]); setCoord({ firstName: "", lastName: "" }); setConsentRdv(false);
    setAppt({ date: "", time: "" }); setAppointmentStart(null); setChosenPartner(null);
    setHeld(null); setBooking(null); setShowNameForm(false);
    setNeedSlug(null); setMedOption(null); setMotif(null); setAnswers({}); setAlertFlag(false);
    setIdleWarn(false); setStep("mt_indispo");
    navigate("/");
  }, [navigate]);

  const stayActive = useCallback(() => { lastActivity.current = Date.now(); setIdleWarn(false); }, []);

  useEffect(() => {
    const bump = () => { lastActivity.current = Date.now(); setIdleWarn(false); };
    const evs = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, bump));
  }, []);

  useEffect(() => {
    if (!isSensitive) { setIdleWarn(false); return; }
    lastActivity.current = Date.now();
    const t = setInterval(() => {
      const idle = (Date.now() - lastActivity.current) / 1000;
      if (idle >= 60) { resetSession(); }
      else if (idle >= 50) { setIdleWarn(true); setIdleLeft(Math.max(0, Math.ceil(60 - idle))); }
    }, 1000);
    return () => clearInterval(t);
  }, [isSensitive, step, resetSession]);

  const session = isSensitive
    ? { sensitive: true, endSession: resetSession, idleWarn, idleLeft, stay: stayActive }
    : null;

  // URL de reprise sur téléphone personnel (aucune donnée personnelle transmise)
  const phoneUrl = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/p/${pharmacySlug || "pharmacie-des-ecoles"}/teleconsultation-specialiste?etape=specialites`;
  }, [pharmacySlug]);

  useEffect(() => {
    api.get(`/public/pharmacy/${pharmacySlug}`).then((r) => setConfig(r.data)).catch(() => {
      api.get("/public/config").then((r) => setConfig(r.data)).catch(() => setNotFound(true));
    });
  }, [pharmacySlug]);

  useEffect(() => {
    if (!held?.heldUntil) return;
    const tick = () => setCountdown(Math.max(0, Math.round((new Date(held.heldUntil).getTime() - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [held]);

  const specs = useMemo(() => config?.specialites || [], [config]);
  const partners = config?.partners || [];
  const specName = useMemo(() => {
    const found = specs.find((s) => s.slug === specSlug)?.name;
    if (found) return found;
    if (specSlug === GENERALIST_SLUG) return "médecin généraliste";
    return undefined;
  }, [specs, specSlug]);
  const filteredSpecs = useMemo(() => {
    const n = specQuery.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    return specs.filter((s) => s.name.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").includes(n));
  }, [specQuery, specs]);

  const filteredPartners = partners;

  const startFlow = async () => {
    if (!fromPhone) { setStep("qr_telephone"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/public/requests", {
        requestType: "teleconsultation_specialiste", pharmacyId: config?.pharmacyId || null });
      setReq(data);
      setStep("specialite");
    } catch (e) { err(e); } finally { setLoading(false); }
  };

  const phoneStarted = useRef(false);
  useEffect(() => {
    if (fromPhone && config && !phoneStarted.current) { phoneStarted.current = true; startFlow(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromPhone, config]);

  const chosenPartnerName = useMemo(() => partners.find((p) => p.slug === chosenPartner)?.name, [partners, chosenPartner]);

  const chooseSpec = async () => {
    if (!specSlug) { toast.error("Choisissez une spécialité."); return; }
    countSpecialty(specSlug, config?.pharmacyId);
    setStep("recherche");
  };

  const submitHoraire = async () => {
    if (!coord.firstName.trim() || !coord.lastName.trim()) { toast.error("Renseignez votre prénom et votre nom."); return; }
    if (!appt.time) { toast.error("Indiquez l'heure du rendez-vous trouvé."); return; }
    if (!consentRdv) { toast.error("Merci d'accepter le traitement de vos données."); return; }
    setLoading(true);
    try {
      const date = appt.date || new Date().toISOString().slice(0, 10);
      const dow = new Date(date + "T12:00:00").getDay(); // 0=dim, 6=sam
      if (dow === 0 || dow === 6) {
        toast.error("La cabine est fermée le week-end. Merci de choisir un jour de semaine (lundi à vendredi).");
        setLoading(false);
        return;
      }
      await api.post(`/public/requests/${req.id}/partner-appointment`, { date, time: appt.time, durationMinutes: 30, platform: chosenPartner });
      const { data } = await api.get(`/public/requests/${req.id}/available-slots`);
      setSlots(data.slots || []);
      setAppointmentStart(data.appointmentStart);
      setStep("cabine");
    } catch (e) { err(e); } finally { setLoading(false); }
  };

  const holdSlot = async (slot) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/public/slots/${slot.slotId}/hold?rid=${req.id}`, {
        patientFirstName: coord.firstName, patientLastName: coord.lastName });
      setHeld({ slotId: slot.slotId, cabinName: data.cabinName, startAt: data.startAt, endAt: data.endAt, heldUntil: data.heldUntil, isTest: slot.isTest });
      setStep("creneau_confirme");
    } catch (e) {
      err(e);
      if (e.response?.status === 409) { const { data } = await api.get(`/public/requests/${req.id}/available-slots`); setSlots(data.slots || []); }
    } finally { setLoading(false); }
  };

  const buildPartnerUrl = (slug) => {
    const p = partners.find((x) => x.slug === slug);
    const spec = specs.find((s) => s.slug === specSlug);
    if (slug === "doctolib") {
      const docSlug = spec?.partnerMappings?.doctolib || "medecin-generaliste";
      return `https://www.doctolib.fr/${docSlug}/france?telehealth=true`;
    }
    if (slug === "qare") {
      return spec?.partnerMappings?.qare || p?.baseUrl || "https://www.qare.fr/recherche-praticien?localisation=all&page=1&resultatsParPage=10";
    }
    return p?.baseUrl || "https://www.qare.fr/recherche-praticien?localisation=all&page=1&resultatsParPage=10";
  };

  const ensureRequest = async () => {
    if (req?.id) return req;
    const { data } = await api.post("/public/requests", {
      requestType: "teleconsultation_specialiste", pharmacyId: config?.pharmacyId || null });
    setReq(data);
    return data;
  };

  // Rejoint le flux existant (plateforme → cabine → HOLD) pour une spécialité donnée.
  const goToPlatformWithSpec = async (slug) => {
    setLoading(true);
    try {
      await ensureRequest();
      setSpecSlug(slug);
      countSpecialty(slug, config?.pharmacyId);
      setStep("recherche");
    } catch (e) { err(e); } finally { setLoading(false); }
  };

  const goSpecFromNeed = () => {
    if (!needSlug) { toast.error("Choisissez votre besoin."); return; }
    if (needSlug === "__autre") { startFlow(); return; }
    goToPlatformWithSpec(needSlug);
  };

  const submitQuestions = () => {
    setAlertFlag(answers.fievreResp === "oui");
    setStep("solutions");
  };

  const selectPartner = (slug) => {
    setChosenPartner(slug);
    setStep("avant_redirection");
  };

  const doRedirect = () => {
    const url = buildPartnerUrl(chosenPartner || "doctolib");
    openExternal(url);
    api.post(`/public/requests/${req.id}/redirect?partnerSlug=${chosenPartner}&locationSlug=${DOCTOLIB_LOCATION_SLUG}`).catch(() => {});
    setStep("horaire");
  };

  const reopenPartner = () => {
    const url = buildPartnerUrl(chosenPartner || "doctolib");
    openExternal(url);
  };

  const confirmCabin = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/public/requests/${req.id}/confirm-cabin`);
      setBooking(data.booking);
      setStep("confirmation");
    } catch (e) { err(e); if (e.response?.status === 409) setStep("cabine_expiree"); }
    finally { setLoading(false); }
  };

  const releaseAndReselect = async () => {
    try { await api.post(`/public/requests/${req.id}/release`); } catch (e) {}
    setHeld(null);
    const { data } = await api.get(`/public/requests/${req.id}/available-slots`);
    setSlots(data.slots || []);
    setStep("cabine");
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (notFound) return <Shell><div className="text-center py-10"><AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-3" /><p className="text-muted-foreground">Pharmacie introuvable.</p></div></Shell>;
  if (!config) return <Shell><p className="text-muted-foreground">Chargement…</p></Shell>;
  const pharmacyName = config.pharmacie?.name;

  // 1) Écran d'entrée : attestation obligatoire (respect du parcours de soins)
  if (step === "mt_indispo") return (
    <Shell progress={10} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => navigate("/")} />
      <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary border border-primary/30 px-3 py-1 text-sm font-semibold mb-4"><MapPin className="h-4 w-4" /> {config.pharmacie?.city}</div>
      <h1 className="font-heading text-2xl font-bold mb-3">Accès à la téléconsultation spécialiste</h1>
      <p className="text-sm text-muted-foreground mb-2">Ce service s'adresse aux patients dont le médecin traitant n'est pas disponible.</p>
      <p className="text-sm text-muted-foreground mb-6">Pour continuer, confirmez ci-dessous que votre médecin traitant n'est pas disponible actuellement.</p>
      <Button data-testid="tc-gate-confirm" onClick={startFlow} disabled={loading} className="w-full h-16 rounded-2xl text-base font-semibold gap-2 whitespace-normal leading-tight py-3">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Mon médecin traitant n'est pas disponible <ArrowRight className="h-5 w-5 shrink-0" /></>}
      </Button>
      <div className="flex items-start gap-2 rounded-xl bg-primary/5 border border-primary/20 p-4 mt-4" data-testid="tc-confidential-note">
        <ShieldAlert className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <p className="text-sm text-foreground/80 leading-relaxed">Pour prendre rendez-vous pour une téléconsultation dans notre pharmacie, notre équipe peut vous accompagner dans un espace confidentiel.</p>
      </div>
      <InfoBox>Cette borne vous aide à rechercher une solution de soins. Elle ne remplace pas l'évaluation d'un professionnel de santé. L'équipe de la pharmacie peut vous accompagner dans l'utilisation de la borne.</InfoBox>
      <ReimbursementNote />
      <UrgenceLink onClick={() => setStep("urgences")} />
    </Shell>
  );

  // 2A) Consulter un médecin rapidement
  if (step === "consulter_medecin") {
    const OPTS = [
      { key: "tele", label: "Téléconsultation disponible" },
      { key: "cabinet", label: "Rendez-vous au cabinet" },
      { key: "dispo", label: "Voir les prochaines disponibilités" },
    ];
    return (
      <Shell progress={30} pharmacyName={pharmacyName} session={session}>
        <Back onClick={() => setStep("mt_indispo")} />
        <h1 className="font-heading text-2xl font-bold mb-2">Consulter un médecin rapidement</h1>
        <p className="text-sm text-muted-foreground mb-4">Choisissez la solution que vous souhaitez rechercher.</p>
        <div className="space-y-2 mb-5">
          {OPTS.map((o) => (
            <button key={o.key} data-testid={`tc-cm-${o.key}`} onClick={() => setMedOption(o.key)}
              className={`w-full text-left rounded-xl border-2 p-4 text-sm transition-colors ${medOption === o.key ? "border-primary bg-primary/5 text-primary font-semibold" : "hover:border-primary/50"}`}>{o.label}</button>
          ))}
        </div>
        <p className="text-sm font-medium mb-2">Motif de votre recherche <span className="text-muted-foreground font-normal">(facultatif)</span></p>
        <div className="flex flex-wrap gap-2 mb-2">
          {MOTIFS.map((m) => (
            <button key={m} data-testid={`tc-motif-${m}`} onClick={() => setMotif(motif === m ? null : m)}
              className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${motif === m ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>{m}</button>
          ))}
        </div>
        <Button data-testid="tc-cm-continue" onClick={() => goToPlatformWithSpec(GENERALIST_SLUG)} disabled={loading || !medOption} className="w-full h-14 rounded-2xl gap-2 mt-4">Voir les solutions disponibles <ArrowRight className="h-5 w-5" /></Button>
        <LegalLine>Le médecin reste seul responsable de la consultation, du diagnostic et des prescriptions éventuelles.</LegalLine>
        <UrgenceLink onClick={() => setStep("urgences")} />
      </Shell>
    );
  }

  // 2B) Je pense avoir besoin d'un spécialiste
  if (step === "besoin_specialiste") return (
    <Shell progress={30} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => setStep("mt_indispo")} />
      <h1 className="font-heading text-2xl font-bold mb-4">Quel est votre besoin ?</h1>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {SPEC_NEEDS.map((n) => {
          const Icon = n.icon;
          return (
            <button key={n.slug} data-testid={`tc-need-${n.slug}`} onClick={() => setNeedSlug(n.slug)}
              className={`rounded-xl border-2 p-4 text-sm text-left flex flex-col gap-2 transition-colors ${needSlug === n.slug ? "border-primary bg-primary/5 text-primary font-semibold" : "hover:border-primary/50"}`}>
              <Icon className="h-5 w-5" /> {n.label}
            </button>
          );
        })}
      </div>
      <InfoBox>Selon la spécialité et votre situation, une orientation préalable par un médecin peut être nécessaire. Vous exprimez ici uniquement ce que vous recherchez.</InfoBox>
      <Button data-testid="tc-need-continue" onClick={goSpecFromNeed} disabled={loading || !needSlug} className="w-full h-14 rounded-2xl gap-2 mt-4">Continuer <ArrowRight className="h-5 w-5" /></Button>
      <HelpNote />
      <UrgenceLink onClick={() => setStep("urgences")} />
    </Shell>
  );

  // 4) J'ai besoin de soins rapidement
  if (step === "soins_rapides") return (
    <Shell progress={20} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => setStep("mt_indispo")} />
      <h1 className="font-heading text-2xl font-bold mb-1">J'ai besoin de soins rapidement</h1>
      <p className="text-sm text-muted-foreground mb-5">Nous allons vous aider à rechercher la bonne démarche.</p>
      <div className="space-y-3">
        <BigCard testid="tc-sr-avis" icon={UserRound} title="J'ai besoin d'un avis médical" onClick={() => setStep("questions")} />
        <BigCard testid="tc-sr-spec" icon={Stethoscope} title="Je pense avoir besoin d'un spécialiste" onClick={() => setStep("besoin_specialiste")} />
        <BigCard testid="tc-sr-unknown" icon={HelpCircle} title="Je ne sais pas vers qui me tourner" onClick={() => setStep("questions")} />
      </div>
      <InfoBox>Cette borne constitue un outil d'aide à la recherche d'une solution et ne remplace pas une consultation médicale.</InfoBox>
      <UrgenceLink onClick={() => setStep("urgences")} />
    </Shell>
  );

  // 5) Questions simples (navigation uniquement, pas de diagnostic)
  if (step === "questions") {
    const QUESTIONS = [
      { id: "duree", q: "Depuis combien de temps avez-vous ce problème ?", opts: [["auj", "Aujourd'hui"], ["2-3j", "2 à 3 jours"], ["long", "Plus longtemps"]] },
      { id: "douleur", q: "Votre douleur est-elle importante ?", opts: [["oui", "Oui"], ["moderee", "Modérée"], ["non", "Non"]] },
      { id: "fievreResp", q: "Avez-vous de la fièvre ou des difficultés à respirer ?", opts: [["oui", "Oui"], ["non", "Non"]] },
    ];
    const allAnswered = QUESTIONS.every((q) => answers[q.id]);
    return (
      <Shell progress={38} pharmacyName={pharmacyName} session={session}>
        <Back onClick={() => setStep("soins_rapides")} />
        <h1 className="font-heading text-2xl font-bold mb-4">Quelques questions</h1>
        <div className="space-y-5">
          {QUESTIONS.map((q) => (
            <div key={q.id}>
              <p className="text-sm font-medium mb-2">{q.q}</p>
              <div className="flex flex-wrap gap-2">
                {q.opts.map(([val, lbl]) => (
                  <button key={val} data-testid={`tc-q-${q.id}-${val}`} onClick={() => setAnswers((a) => ({ ...a, [q.id]: val }))}
                    className={`text-sm font-medium rounded-xl px-4 py-2.5 border-2 transition-colors ${answers[q.id] === val ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>{lbl}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button data-testid="tc-q-continue" onClick={submitQuestions} disabled={!allAnswered} className="w-full h-14 rounded-2xl gap-2 mt-6">Voir les solutions disponibles <ArrowRight className="h-5 w-5" /></Button>
        <LegalLine>{NON_DIAGNOSTIC_NOTE}</LegalLine>
        <UrgenceLink onClick={() => setStep("urgences")} />
      </Shell>
    );
  }

  // 7) Solutions disponibles
  if (step === "solutions") return (
    <Shell progress={50} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => setStep("mt_indispo")} />
      <h1 className="font-heading text-2xl font-bold mb-4">Solutions disponibles</h1>
      {alertFlag && (
        <Card className="rounded-2xl p-4 mb-4 bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800" data-testid="tc-solutions-alert">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-rose-700 dark:text-rose-400">Certains éléments peuvent nécessiter une prise en charge urgente.</p>
              <a href="tel:15"><Button data-testid="tc-solutions-call15" variant="destructive" className="mt-2 h-11 rounded-xl gap-2"><Phone className="h-4 w-4" /> Appeler le 15</Button></a>
            </div>
          </div>
        </Card>
      )}
      <div className="space-y-3">
        <BigCard testid="tc-sol-tele" icon={Video} title="Téléconsultation avec un médecin" subtitle="Rechercher une téléconsultation" onClick={() => goToPlatformWithSpec(GENERALIST_SLUG)} disabled={loading} />
        <BigCard testid="tc-sol-spec" icon={Stethoscope} title="Rechercher un spécialiste" subtitle="Choisir une spécialité" onClick={startFlow} disabled={loading} />
        <BigCard testid="tc-sol-urgence" icon={ShieldAlert} title="Urgences / 15" subtitle="En cas de signe d'alerte" onClick={() => setStep("urgences")} />
      </div>
      <LegalLine>Il ne s'agit pas d'un diagnostic médical. La décision médicale appartient au professionnel de santé consulté.</LegalLine>
      <HelpNote />
    </Shell>
  );

  // 6) Écran de sécurité / urgences
  if (step === "urgences") return (
    <Shell pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => setStep("mt_indispo")} />
      <div className="h-14 w-14 rounded-2xl bg-rose-500/10 grid place-items-center text-rose-600 mb-4"><ShieldAlert className="h-7 w-7" /></div>
      <h1 className="font-heading text-2xl font-bold mb-3">Certains symptômes nécessitent une prise en charge médicale urgente</h1>
      <ul className="text-sm text-muted-foreground space-y-1.5 mb-5 list-disc pl-5">
        <li>douleur thoracique intense</li>
        <li>difficulté importante à respirer</li>
        <li>malaise ou perte de connaissance</li>
        <li>saignement important</li>
        <li>autre symptôme grave ou brutal</li>
      </ul>
      <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-300 dark:border-rose-800 p-4 mb-4">
        <p className="text-sm font-semibold text-rose-700 dark:text-rose-400">En cas de danger immédiat ou d'urgence vitale : appelez le 15 ou le 112.</p>
      </div>
      <a href="tel:15"><Button data-testid="tc-urgence-call15" variant="destructive" className="w-full h-14 rounded-2xl gap-2 text-lg"><Phone className="h-5 w-5" /> Appeler le 15</Button></a>
      <LegalLine>La borne ne doit pas retarder l'appel aux services d'urgence.</LegalLine>
    </Shell>
  );

  // 1bis) Borne : la suite du parcours se fait uniquement sur le téléphone du patient
  if (step === "qr_telephone") return (
    <Shell progress={18} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => setStep("mt_indispo")} />
      <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center text-primary mb-4"><Smartphone className="h-7 w-7" /></div>
      <h1 className="font-heading text-2xl font-bold mb-2">Poursuivez sur votre téléphone</h1>
      <p className="text-sm text-muted-foreground mb-6">Scannez ce QR code avec l'appareil photo de votre téléphone : vous choisirez votre spécialiste et finaliserez votre démarche en toute discrétion.</p>
      <Card className="rounded-2xl p-6 flex flex-col items-center gap-4 mb-4" data-testid="tc-phone-qr">
        <div className="bg-white p-3 rounded-xl border"><QRCodeSVG value={phoneUrl} size={220} level="M" bgColor="#ffffff" fgColor="#0f172a" /></div>
        <p className="text-xs text-muted-foreground text-center leading-relaxed">Aucune donnée personnelle ni médicale n'est contenue dans ce code.</p>
      </Card>
      <Button data-testid="tc-qr-home" variant="ghost" onClick={resetSession} className="w-full h-12 rounded-2xl mt-2 text-muted-foreground">Retour à l'accueil</Button>
    </Shell>
  );

  // 1ter) Accompagnement pharmacien — aucune saisie d'information personnelle
  if (step === "accompagnement") return (
    <Shell progress={18} pharmacyName={pharmacyName}>
      <Back onClick={() => setStep("qr_telephone")} />
      <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center text-primary mb-4"><UserRound className="h-7 w-7" /></div>
      <h1 className="font-heading text-2xl font-bold mb-3">Demandez l'aide d'un pharmacien</h1>
      <p className="text-sm text-muted-foreground mb-4">Adressez-vous à notre équipe au comptoir. Un pharmacien vous accompagnera dans un espace confidentiel pour organiser votre téléconsultation, sans que vos informations soient visibles par d'autres personnes.</p>
      <div className="rounded-xl bg-secondary/5 border border-secondary/20 p-4 mb-6 flex gap-2">
        <Info className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground leading-relaxed">Aucune information personnelle n'est saisie sur cette borne tant que vous n'êtes pas dans un cadre confidentiel.</p>
      </div>
      <Button data-testid="tc-accomp-home" onClick={() => navigate("/")} className="w-full h-14 rounded-2xl gap-2"><ArrowLeft className="h-5 w-5" /> Retour à l'accueil</Button>
    </Shell>
  );

  // 2) Choix de la spécialité
  if (step === "specialite") return (
    <Shell progress={28} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => (fromPhone ? navigate("/") : setStep("mt_indispo"))} />
      <h1 className="font-heading text-2xl font-bold mb-2">Choisissez un spécialiste</h1>
      <p className="text-sm text-muted-foreground mb-4">Sélectionnez la spécialité. Vous chercherez ensuite votre rendez-vous sur une plateforme, puis reviendrez réserver votre cabine.</p>
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input data-testid="tc-spec-search" value={specQuery} onChange={(e) => setSpecQuery(e.target.value)} placeholder="Rechercher une spécialité" className="h-14 pl-12 rounded-2xl border-2" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-6">
        {filteredSpecs.map((s) => (
          <button key={s.slug} data-testid={`tc-spec-${s.slug}`} onClick={() => setSpecSlug(s.slug)}
            className={`rounded-xl border-2 p-3 text-sm text-left ${specSlug === s.slug ? "border-primary bg-primary/5 text-primary font-semibold" : "hover:border-primary/50"}`}>{s.name}</button>
        ))}
        {filteredSpecs.length === 0 && <p className="text-sm text-muted-foreground col-span-2">Aucune spécialité trouvée.</p>}
      </div>
      <Button data-testid="tc-spec-continue" onClick={chooseSpec} disabled={loading || !specSlug} className="w-full h-14 rounded-2xl gap-2">Continuer <ArrowRight className="h-5 w-5" /></Button>
      <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">L'accès direct et le niveau de remboursement peuvent varier selon la spécialité, votre situation et le parcours de soins.</p>
    </Shell>
  );

  // 3) Recherche du rendez-vous sur Doctolib/Qare (SANS réserver encore)
  if (step === "recherche") return (
    <Shell progress={44} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => setStep("specialite")} />
      <div className="h-14 w-14 rounded-2xl bg-secondary/10 grid place-items-center text-secondary mb-4"><Stethoscope className="h-7 w-7" /></div>
      <h1 className="font-heading text-2xl font-bold mb-2">Trouvez votre rendez-vous {specName}</h1>
      {specSlug && specSlug !== GENERALIST_SLUG && !DIRECT_ACCESS.has(specSlug) && (
        <div className="rounded-xl bg-secondary/5 border border-secondary/20 p-3 mb-4 flex gap-2" data-testid="tc-parcours-note">
          <Info className="h-4 w-4 text-secondary shrink-0 mt-0.5" /><p className="text-xs text-muted-foreground leading-relaxed">Selon votre situation et la spécialité choisie, une orientation préalable par un médecin peut être nécessaire pour respecter le parcours de soins. Le niveau de remboursement peut varier.</p>
        </div>
      )}
      <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 p-3 mb-4 flex gap-2">
        <Info className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground"><b className="text-foreground">Ne réservez pas encore.</b> Repérez d'abord un horaire de téléconsultation disponible sur la plateforme, puis revenez ici pour bloquer votre cabine à cette heure. Vous confirmerez le rendez-vous ensuite.</p>
      </div>
      <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 mb-4 flex gap-2" data-testid="tc-cabin-hours">
        <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">Les cabines de téléconsultation sont disponibles <b className="text-foreground">de 8h à 12h et de 14h à 18h30</b>. Choisissez un rendez-vous dans ces horaires.</p>
      </div>

      {/* Filtres plateformes */}
      <div className="space-y-3" data-testid="tc-platform-list">
        {filteredPartners.length === 0 && <p className="text-sm text-muted-foreground">Aucune plateforme disponible pour le moment.</p>}
        {filteredPartners.map((p) => (
          <button key={p.slug} data-testid={`tc-partner-${p.slug}`} disabled={loading} onClick={() => selectPartner(p.slug)}
            className="w-full text-left rounded-2xl border-2 p-4 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50">
            <div className="flex items-center justify-between">
              <span className="font-heading font-bold text-base flex items-center gap-2"><Video className="h-4 w-4 text-primary" /> {p.name}</span>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </div>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Cette application n'est pas affiliée à ces plateformes : elle assure uniquement une redirection. Aucune donnée médicale n'est transmise.</p>
    </Shell>
  );

  // 9) Écran avant redirection externe
  if (step === "avant_redirection") return (
    <Shell progress={52} pharmacyName={pharmacyName} session={session}>
      <Back onClick={() => setStep("recherche")} />
      <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center text-primary mb-4"><ExternalLink className="h-7 w-7" /></div>
      <h1 className="font-heading text-2xl font-bold mb-3">Vous allez être redirigé vers le service de réservation</h1>
      <Card className="rounded-2xl p-4 mb-4 space-y-2 text-sm" data-testid="tc-redirect-checklist">
        <p className="font-medium mb-1">Vérifiez :</p>
        <p className="flex items-start gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> la spécialité choisie{specName ? <b className="ml-1">({specName})</b> : null}</p>
        <p className="flex items-start gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> le type de consultation</p>
        <p className="flex items-start gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> la date et l'heure</p>
        <p className="flex items-start gap-2"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> les conditions tarifaires et de remboursement indiquées par le professionnel ou la plateforme</p>
      </Card>
      <Button data-testid="tc-redirect-continue" onClick={doRedirect} className="w-full h-14 rounded-2xl gap-2"><ExternalLink className="h-5 w-5" /> Continuer vers la réservation</Button>
      {!fromPhone && <>
        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 my-4 flex gap-2" data-testid="tc-redirect-privacy">
          <ShieldAlert className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/80 leading-relaxed">Évitez de vous connecter à un compte médical personnel sur cette borne partagée. Effectuez de préférence votre réservation sur votre téléphone, ou avec l'aide d'un pharmacien dans un espace dédié.</p>
        </div>
        <PhoneContinue url={phoneUrl} testid="tc-redirect-qr" />
      </>}
      <HelpNote />
      <LegalLine>La pharmacie ne réalise pas le diagnostic médical et ne garantit pas la prise en charge par l'Assurance Maladie. Les plateformes sont indépendantes d'EYMI et gèrent leurs propres comptes et réservations.</LegalLine>
    </Shell>
  );

  // 4) Retour : saisir l'horaire du RDV trouvé + coordonnées
  if (step === "horaire") {
    const canSubmit = coord.firstName.trim() && coord.lastName.trim() && appt.time && consentRdv;
    return (
      <Shell cabin progress={60} pharmacyName={pharmacyName} session={session}>
        <Back onClick={() => setStep("recherche")} />
        <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center text-primary mb-4"><CalendarPlus className="h-7 w-7" /></div>
        <h1 className="font-heading text-2xl font-bold mb-1">Quel horaire avez-vous trouvé ?</h1>
        <p className="text-sm text-muted-foreground mb-6">Indiquez la date et l'heure de la téléconsultation repérée sur {chosenPartnerName || "la plateforme"}. Nous vous proposerons une cabine à cette heure — vous réserverez le rendez-vous juste après.</p>
        <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 mb-5 flex gap-2">
          <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">Cabines disponibles <b className="text-foreground">de 8h à 12h et de 14h à 18h30</b>.</p>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-sm">Date du RDV</Label>
              <Input data-testid="tc-appt-date" type="date" value={appt.date} onChange={(e) => setAppt({ ...appt, date: e.target.value })} className="h-12 rounded-xl" /></div>
            <div className="space-y-1.5"><Label className="text-sm">Heure du RDV</Label>
              <Input data-testid="tc-appt-time" type="time" value={appt.time} onChange={(e) => setAppt({ ...appt, time: e.target.value })} className="h-12 rounded-xl" /></div>
          </div>
        </div>

        {!fromPhone && <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 mt-5 flex gap-2" data-testid="tc-name-privacy">
          <EyeOff className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-foreground/80 leading-relaxed">Votre nom n'est pas demandé directement sur cette borne partagée. Finalisez sur votre téléphone, ou demandez à un pharmacien de vous accompagner avant de le saisir.</p>
        </div>}

        {!showNameForm ? (
          <div className="space-y-3 mt-4">
            <PhoneContinue url={phoneUrl} testid="tc-horaire-qr" />
            <Button data-testid="tc-reveal-name" variant="outline" onClick={() => setShowNameForm(true)} className="w-full h-14 rounded-2xl gap-2"><UserRound className="h-5 w-5" /> Saisir mon nom avec l'aide d'un pharmacien</Button>
            <Button data-testid="tc-horaire-reopen" variant="ghost" onClick={reopenPartner} className="w-full h-12 rounded-2xl text-muted-foreground gap-2"><ExternalLink className="h-4 w-4" /> Rouvrir {chosenPartnerName || "la plateforme"}</Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="space-y-1.5"><Label className="text-sm">Prénom</Label>
                <Input data-testid="tc-coord-firstname" value={coord.firstName} onChange={(e) => setCoord({ ...coord, firstName: e.target.value })} className="h-12 rounded-xl" /></div>
              <div className="space-y-1.5"><Label className="text-sm">Nom</Label>
                <Input data-testid="tc-coord-lastname" value={coord.lastName} onChange={(e) => setCoord({ ...coord, lastName: e.target.value })} className="h-12 rounded-xl" /></div>
            </div>
            <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="tc-consent">
              <input type="checkbox" checked={consentRdv} onChange={(e) => setConsentRdv(e.target.checked)} className="mt-0.5 h-5 w-5 rounded border-2 accent-primary shrink-0" data-testid="tc-consent-checkbox" />
              <span className="text-xs text-muted-foreground leading-relaxed">{RDV_CONSENT}</span>
            </label>
            <Button data-testid="tc-horaire-continue" onClick={submitHoraire} disabled={loading || !canSubmit} className="w-full h-14 rounded-2xl gap-2 mt-6">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Voir les cabines disponibles <ArrowRight className="h-5 w-5" /></>}
            </Button>
            <Button data-testid="tc-horaire-reopen" variant="ghost" onClick={reopenPartner} className="w-full h-12 rounded-2xl mt-2 text-muted-foreground gap-2"><ExternalLink className="h-4 w-4" /> Rouvrir {chosenPartnerName || "la plateforme"}</Button>
          </>
        )}
      </Shell>
    );
  }

  // 5) Créneau de cabine — uniquement à l'heure exacte du rendez-vous
  if (step === "cabine") {
    const exactSlots = slots.filter((s) => s.deltaMinutes === 0);
    return (
      <Shell cabin progress={74} pharmacyName={pharmacyName} session={session}>
        <Back onClick={() => setStep("horaire")} />
        <div className="h-14 w-14 rounded-2xl bg-secondary/10 grid place-items-center text-secondary mb-4"><Box className="h-7 w-7" /></div>
        <h1 className="font-heading text-2xl font-bold mb-2">Cabine pour votre rendez-vous</h1>
        <div className="flex items-center gap-1.5 text-xs text-primary bg-primary/5 rounded-lg px-3 py-2 mb-5"><Clock className="h-3.5 w-3.5" /> {specName} · RDV à {hmFr(appointmentStart)}</div>
        {exactSlots.length === 0 ? (
          <div className="text-center py-8" data-testid="tc-no-cabin">
            <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-3" />
            <p className="text-sm text-muted-foreground mb-1">Aucune cabine n'est disponible à <b>{hmFr(appointmentStart)}</b>.</p>
            <p className="text-sm text-muted-foreground">Veuillez choisir un autre horaire de téléconsultation sur la plateforme, puis revenez l'indiquer ici.</p>
            <Button data-testid="tc-cabine-back" onClick={() => setStep("horaire")} className="mt-5 h-12 rounded-2xl gap-2"><ArrowLeft className="h-4 w-4" /> Choisir un autre horaire</Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">Une cabine est libre à l'heure de votre rendez-vous. Elle sera <b>bloquée 15 minutes</b> le temps de finaliser.</p>
            <div className="space-y-2" data-testid="tc-slots">
              {exactSlots.map((s) => (
                <button key={s.slotId} data-testid={`tc-slot-${s.slotId}`} disabled={loading} onClick={() => holdSlot(s)}
                  className="w-full flex items-center justify-between rounded-2xl border-2 p-4 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50">
                  <span className="flex items-center gap-2 font-medium"><Box className="h-4 w-4 text-secondary" /> {s.cabinName}</span>
                  <span className="font-heading font-bold text-primary">{hmFr(s.startAt)}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </Shell>
    );
  }

  // 6) Confirmation du créneau cabine (blocage) → puis réservation du RDV
  if (step === "creneau_confirme") return (
    <Shell cabin progress={86} pharmacyName={pharmacyName} session={session}>
      <Card className="rounded-2xl p-4 mb-5 bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800" data-testid="tc-hold-banner">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-500/20 grid place-items-center text-amber-700 dark:text-amber-400 shrink-0"><Clock className="h-5 w-5" /></div>
          <div><p className="text-sm font-semibold">Cabine bloquée pour vous</p>
            <p className="text-xs text-muted-foreground">Expire dans <b data-testid="tc-countdown">{mmss(countdown)}</b></p></div>
        </div>
      </Card>
      <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center text-primary mb-4"><Box className="h-7 w-7" /></div>
      <h1 className="font-heading text-2xl font-bold mb-3">Confirmez votre cabine</h1>
      <Card className="rounded-2xl p-5 mb-4 bg-primary/5 border-primary/30" data-testid="tc-creneau-recap">
        {held?.isTest && <span data-testid="tc-test-badge" className="inline-block text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 rounded-full px-2 py-0.5 mb-2">Mode test</span>}
        <p className="text-base leading-relaxed">Cabine <b className="text-primary">{held?.cabinName}</b> à <b>{hmFr(held?.startAt)}</b>, pour votre rendez-vous {specName} de <b>{hmFr(appointmentStart)}</b>.</p>
      </Card>
      <div className="rounded-xl bg-secondary/5 border border-secondary/20 p-4 mb-6 flex gap-3">
        <Info className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">Confirmez votre cabine maintenant. <b className="text-foreground">Vous réserverez ensuite votre rendez-vous sur {chosenPartnerName || "la plateforme"}</b> à {hmFr(appointmentStart)}.</p>
      </div>
      <Button data-testid="tc-confirm-cabin" onClick={confirmCabin} disabled={loading || countdown <= 0} className="w-full h-14 rounded-2xl gap-2"><Check className="h-5 w-5" /> Confirmer ma cabine</Button>
      <Button data-testid="tc-creneau-release" variant="ghost" onClick={releaseAndReselect} className="w-full h-12 rounded-2xl mt-2 text-muted-foreground">Choisir un autre créneau</Button>
      {countdown <= 0 && <p className="text-xs text-rose-600 mt-3">Le blocage a expiré. Choisissez un nouveau créneau.</p>}
    </Shell>
  );

  // Blocage expiré
  if (step === "cabine_expiree") return (
    <Shell cabin progress={94} pharmacyName={pharmacyName} session={session}>
      <div className="h-14 w-14 rounded-2xl bg-rose-500/10 grid place-items-center text-rose-600 mb-4"><AlertTriangle className="h-7 w-7" /></div>
      <h1 className="font-heading text-2xl font-bold mb-2">Le blocage a expiré</h1>
      <p className="text-sm text-muted-foreground mb-6">Les 15 minutes de réservation temporaire sont écoulées. Choisissez un nouveau créneau de cabine.</p>
      <Button data-testid="tc-reselect" onClick={releaseAndReselect} className="w-full h-14 rounded-2xl">Choisir un nouveau créneau</Button>
    </Shell>
  );

  // 8) Confirmation (cabine RESERVED)
  if (step === "confirmation") {
    const startAt = booking?.startAt || held?.startAt;
    return (
      <Shell cabin progress={100} pharmacyName={pharmacyName} session={session}>
        <div className="text-center mb-6" data-testid="tc-done">
          <div className="h-16 w-16 rounded-full bg-primary/10 grid place-items-center mx-auto mb-3"><Check className="h-8 w-8 text-primary" /></div>
          <h1 className="font-heading text-2xl font-bold">Cabine confirmée</h1>
        </div>
        <Card className="rounded-2xl p-5 mb-4 bg-primary/5 border-primary/30" data-testid="tc-cabin-assignment">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center text-primary-foreground shrink-0"><Box className="h-5 w-5" /></div>
            <div className="space-y-1.5">
              {held?.isTest && <span data-testid="tc-confirm-test-badge" className="inline-block text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 rounded-full px-2 py-0.5">Mode test</span>}
              <p className="text-base leading-relaxed">Votre téléconsultation de <b>{hmFr(startAt)}</b> se déroulera au sein de la <b className="text-primary">{held?.cabinName}</b>.</p>
            </div>
          </div>
        </Card>

        {/* Dernière étape : réserver le rendez-vous sur la plateforme */}
        <Card className="rounded-2xl p-5 mb-4 bg-secondary/5 border-secondary/30" data-testid="tc-book-rdv">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-secondary grid place-items-center text-secondary-foreground shrink-0"><Stethoscope className="h-5 w-5" /></div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary mb-0.5">Dernière étape</p>
              <p className="text-sm leading-relaxed">Réservez maintenant votre rendez-vous <b>{specName}</b> sur <b>{chosenPartnerName || "la plateforme"}</b> à <b>{hmFr(startAt)}</b>, pour qu'il corresponde à votre cabine.</p>
            </div>
          </div>
          <Button data-testid="tc-book-rdv-btn" onClick={reopenPartner} className="w-full h-14 rounded-2xl gap-2"><ExternalLink className="h-5 w-5" /> Réserver mon rendez-vous sur {chosenPartnerName || "la plateforme"}</Button>
        </Card>

        <Card className="rounded-2xl p-5 mb-4 space-y-1.5 text-sm">
          <p>Spécialité : <b>{specName}</b></p>
          <p>Cabine : <b>{held?.cabinName}</b></p>
          <p>Heure : <b>{hmFr(startAt)}</b></p>
          <p>Patient : <b>{coord.firstName} {coord.lastName}</b></p>
        </Card>
        <p className="text-sm text-muted-foreground mb-4">Présentez-vous à l'heure indiquée avec votre carte Vitale. Pensez à finaliser la réservation de votre rendez-vous sur la plateforme.</p>
        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="tc-copy" variant="outline" onClick={() => { navigator.clipboard.writeText(`${specName || ""} — Cabine ${held?.cabinName || ""} à ${hmFr(startAt)} — ${config.pharmacie?.address || ""}`); toast.success("Récapitulatif copié"); }} className="h-12 rounded-xl gap-1.5"><Copy className="h-4 w-4" /> Copier</Button>
          {config.pharmacie?.phone && <a href={`tel:${config.pharmacie.phone.replace(/\s/g, "")}`}><Button variant="outline" className="w-full h-12 rounded-xl gap-1.5"><Phone className="h-4 w-4" /> Appeler</Button></a>}
        </div>
        <Button data-testid="tc-finish-session" variant="ghost" onClick={resetSession} className="w-full h-12 rounded-2xl mt-4 text-muted-foreground gap-2"><X className="h-4 w-4" /> Terminer ma session</Button>
      </Shell>
    );
  }

  return null;
}
